<template>
  <b-card no-body class="mb-2">
    <b-card-body class="search-panel">
      <div v-if="!portId" class="search-panel__no-port">
        No port selected, please search for a port/berth:
      </div>

      <div class="search-panel__controls">
        <!-- port/berth search -->
        <port-search-widget
          title="Port / Berth"
          class="port-search"
          :search-results="searchResults"
          :search-value="searchValue"
          @input="handleSearchInput" />

        <!-- direction (import/export) -->
        <span class="mr-2">{{ searchSchema.direction.label }}</span>
        <template v-for="item in searchSchema.direction.choices">
          <b-button v-if="item.value"
                    :key="`button-${item.value}`"
                    :disabled="apiRequestIsPerforming"
                    :pressed="searchSchema.direction.value === item.value"
                    @click="handleDirectionInput(item)"
                    variant="primary"
                    size="sm"
                    class="choice-button mr-1">
            {{ item.text }}
          </b-button>
        </template>
      </div>

    </b-card-body>
  </b-card>
</template>

<script>
import getNested from 'lodash-es/get';
import intersectionBy from 'lodash-es/intersectionBy';
import debounce from 'lodash-es/debounce';
import { mapActions, mapGetters } from 'vuex';
import PortSearchWidget from '@/components/portscomparison/PortSearchWidget';

const debounceDelay = 100;

export default {
  name: 'search-panel',

  components: {
    PortSearchWidget,
  },

  data() {
    return {
      searchValue: null,
      searchResults: defaultSearchResults,
      apiRequestIsPerforming: false,
      searchLoading: false,

      searchSchema: {
        direction: {
          label: 'Direction',
          value: 'export',
          choices: [
            {text: 'Export', value: 'export'},
            {text: 'Import', value: 'import'},
          ],
        },
      },
    };
  },

  methods: {
    ...mapActions('api/presets', [
      'updateCurrentFilterSettings',
    ]),

    handleDirectionInput(item) {
      this.searchSchema.direction.value = item.value;
      this.$emit('direction', this.searchData);
    },

    handleSearchInput(searchData) {
      this.searchValue = searchData.value;
      this.searchResults = searchData.results;
      this.$emit('input', this.searchData);
    },

    handleSearchResultRemoved() {
      this.searchValue = [];
      this.searchResults = [];
      this.$emit('input', this.searchData);
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),
    ...mapGetters('api/presets', ['getActivePreset']),

    portName() {
      return getNested(this, 'searchValue.portName', '');
    },

    portId() {
      return getNested(this, 'searchValue.portId', '');
    },

    unlocode() {
      return getNested(this, 'searchValue.unlocode', '');
    },

    countryCode() {
      return getNested(this, 'searchValue.countryCode', '');
    },

    searchData() {
      return {
        value: this.searchValue,
        results: this.searchResults,
        direction: this.searchSchema.direction.value,
      }
    },

    presetParameters() {
      return {
        searchResults: this.searchResults,
        searchValue: this.searchValue,
        direction: this.searchSchema.direction.value,
      };
    },
  },

  watch: {
    getActiveRequestsCount: debounce(function (newValue) {
      this.apiRequestIsPerforming = !!newValue;
    }, debounceDelay),

    selectedEntities(values) {
      // sync filter-panel berthIds with search-multiselect values
      this.searchValue = intersectionBy(this.searchResults, values, 'id');
    },

    async presetParameters() {
      await this.updateCurrentFilterSettings({
        path: this.$route.path,
        parameters: {
          searchPanel: this.presetParameters,
        },
      });
    },

    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path === preset.viewName) {
          const parameters = getNested(preset, 'params.searchPanel');
          this.searchResults = parameters.searchResults;
          this.searchValue = parameters.searchValue;
          this.searchSchema.direction.value = parameters.direction;
        }
      },
    },
  },
}

const defaultSearchResults = [
  {
    "portId": 1166,
    "portName": "Hong Kong",
    "unlocode": "HKHKG",
    "countryCode": "HK",
    "entityType": "PORT",
    "geomPoint": "{\"type\":\"Point\",\"coordinates\":[114.140890739223,22.3187620830414]}",
    "mbcRadius": 0.331405855207062,
    "id": "port-1166",
    "value": "port-1166",
    "text": "Hong Kong"
  },
];
</script>

<style scoped>
.search-multiselect.multiselect--active {
  min-width: 600px !important;
  transition: all 0.2s;
}
</style>

<style scoped lang="scss">
  .w-10 {
    width: 10%;
  }

  .w-25 {
    width: 25%;
  }

  .search-panel {
    padding: 15px;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    &__no-port {
      margin-right: auto;
      font-size: 18px;
      color: #828282;
    }

    &__title {
      font-size: 18px;
    }

    &__controls {
      display: flex;
      align-items: center;
      min-width: 50%;

      .port-search {
        margin-right: 20px;
        width: 100%;
      }
    }
  }

  .choice-button,
  .choice-button:focus,
  .choice-button:active {
    min-width: 60px;
    outline: none !important;
    box-shadow: none !important;
    background-color: #1d3d4f !important;
    border-color: #1d3d4f !important;

    &.active {
      background-color: #3b89a1 !important;
      border-color: #3b89a1 !important;
    }
  }

</style>
